import { Multiselect, SelectProps } from '@cloudscape-design/components';
import { memo, useEffect, useMemo, useState } from 'react';
import { Division } from 'src/types/orbit/Division';
import { convertList, filterArrayByArray } from 'src/utils/generalUtils';

interface MultiSelectDivisionProps {
  divisions: Division[];
  isLoading: boolean;
  readOnly?: boolean;
  error: string | null;
  selectedDivisionList: string[] | null;
  onItemSelection: (selectedDivisions: Division[]) => void;
}

const MultiSelectDivision = ({
  divisions,
  error,
  isLoading,
  selectedDivisionList,
  readOnly,
  onItemSelection,
}: MultiSelectDivisionProps) => {
  const [selectedDivisions, setSelectedDivisions] = useState<SelectProps.Option[]>([]);

  useEffect(() => {
    if (selectedDivisionList) {
      setSelectedDivisions(
        convertList<Division, SelectProps.Option>(
          divisions.filter((data) => selectedDivisionList.includes(data.divisionName)),
          (item) => ({
            value: item.divisionName,
            label: item.divisionName,
          }),
        ),
      );
    }
    if (divisions.length === 0) {
      onItemSelection([]);
    }
  }, [divisions, selectedDivisionList, onItemSelection]);

  // Memoize the status type calculation
  const statusType = useMemo(() => {
    if (isLoading) return 'loading';
    if (error) return 'error';
    return 'finished';
  }, [isLoading, error]);

  // Memoize the roles list conversion
  const divisionOptions = useMemo(() => {
    return convertList<Division, SelectProps.Option>(divisions, (item) => ({
      value: item.divisionName,
      label: item.divisionName,
    }));
  }, [divisions]);

  return (
    <Multiselect
      readOnly={readOnly}
      data-testid="division-select"
      selectedOptions={selectedDivisions}
      onChange={({ detail }) => {
        const values = detail.selectedOptions as SelectProps.Option[];
        onItemSelection(filterArrayByArray(divisions, values, 'divisionName', 'value'));
        setSelectedDivisions(values);
      }}
      options={divisionOptions}
      errorText={error || undefined}
      loadingText="Loading divisions"
      statusType={statusType}
      placeholder="Choose divisions"
      inlineTokens
    />
  );
};
// Memoize the entire component if needed
export default memo(MultiSelectDivision, (prevProps, nextProps) => {
  // Custom comparison function
  return (
    prevProps.divisions === nextProps.divisions &&
    prevProps.selectedDivisionList === nextProps.selectedDivisionList &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.error === nextProps.error &&
    prevProps.readOnly === nextProps.readOnly
  );
});
